'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui';
import { authService } from '@/features/auth/api/auth';
import { SIGN_IN_METHODS_PATH } from '@/features/auth/lib/sign-in-method-link';
import { useSignInState } from '@/features/auth/model/sign-in.query';
import { AnalyticsEvent, trackEvent } from '@/shared/lib/analytics';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { SignInMethodsSection } from './SignInMethodsSection';

/**
 * Card on the Account page (docs/identity/ADR-001 §5): for store buyers it can
 * be closed; after "Later" on the web step it stays (`sign_in_suggestion_closable`).
 * Title and body come from the API.
 */
export function SignInSuggestionCard() {
  const t = useTranslations('auth.signInMethods');
  const tCommon = useTranslations('common');
  const router = useRouter();
  const queryClient = useQueryClient();
  const state = useSignInState();
  const prompt = state?.suggest_sign_in_method
    ? state.sign_in_method_prompt
    : null;

  useEffect(() => {
    if (prompt)
      trackEvent(AnalyticsEvent.SIGN_IN_METHOD_SUGGESTED, {
        reason: 'suggest',
      });
  }, [prompt]);

  if (!prompt) return null;

  const dismiss = async () => {
    await authService.dismissSignInSuggestion();
    await queryClient.invalidateQueries({ queryKey: cacheKeys.signIn });
  };

  return (
    <NoticeBanner
      variant="info"
      title={prompt.title}
      message={prompt.body}
      action={
        <div className="flex gap-2">
          <Button size="sm" onClick={() => router.push(SIGN_IN_METHODS_PATH)}>
            {t('addCta')}
          </Button>
          {state?.sign_in_suggestion_closable && (
            <Button size="sm" variant="ghost" onClick={dismiss}>
              {tCommon('close')}
            </Button>
          )}
        </div>
      }
    />
  );
}

/**
 * The step after a purchase that cannot be restored without a sign-in method
 * (web checkout, RuStore). A full-page overlay: it goes away once GET /user
 * stops returning `requires_sign_in_method`, either because a method was
 * added or because "Later" (offered only when `sign_in_method_can_defer`)
 * turned it into the Account card.
 */
export function RequiredSignInMethodOverlay() {
  const t = useTranslations('auth.signInMethods');
  const queryClient = useQueryClient();
  const state = useSignInState();
  const required = state?.requires_sign_in_method === true;
  const [deferring, setDeferring] = useState(false);

  useEffect(() => {
    if (required)
      trackEvent(AnalyticsEvent.SIGN_IN_METHOD_SUGGESTED, {
        reason: 'required',
      });
  }, [required]);

  if (!required) return null;

  const defer = async () => {
    setDeferring(true);
    try {
      await authService.deferSignInStep();
      await queryClient.invalidateQueries({ queryKey: cacheKeys.signIn });
    } finally {
      setDeferring(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] overflow-y-auto bg-background"
    >
      <div className="mx-auto max-w-[480px] px-4 py-10 space-y-4">
        {state?.sign_in_method_prompt && (
          <>
            <h1 className="text-section-title text-foreground">
              {state.sign_in_method_prompt.title}
            </h1>
            <p className="text-card-body text-muted-foreground">
              {state.sign_in_method_prompt.body}
            </p>
          </>
        )}
        <SignInMethodsSection prefillEmail={state?.sign_in_prefill_email} />
        {state?.sign_in_method_can_defer && (
          <Button
            variant="ghost"
            className="w-full"
            disabled={deferring}
            onClick={defer}
          >
            {t('later')}
          </Button>
        )}
      </div>
    </div>
  );
}
